import { useEffect, useState } from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import Image from '~/components/Helpers/Image'
import BlockVideo from '~/components/Media/components/BlockVideo'
import MarkDown from '~/components/Helpers/MarkDown'

const blockStyles = cva('', {
  variants: {
    svg: {
      'position-1': 'right-full top-full', // left
      'position-2': 'right-full bottom-full', // top
      'position-3': 'bottom-full left-full', // right
      'position-4': 'top-full left-full' // bottom
    },
    accentColour: {
      'light-blue': 'fill-blue-l',
      'light-aqua': 'fill-green',
      'light-pink': 'fill-pink',
      'light-purple': 'fill-purple',
      'light-orange': 'fill-orange',
      white: 'fill-white'
    },
    aspect: {
      square: 'aspect-square',
      landscape: 'aspect-3/2'
    }
  },
  defaultVariants: {
    svg: 'position-2',
    accentColour: 'white',
    aspect: 'square'
  }
})

type TestProps = {
  image: {
    altText: string
    asset: { sys: { uri: string } }
    caption: string | undefined | null
    transformations: string | undefined | null
  }
  video?: { sys: { uri: string } }
  aspectRatio: 'square' | 'landscape'
  brandDevicePosition: 'position-1' | 'position-2' | 'position-3' | 'position-4'
  brandDeviceColour?:
    | 'light-blue'
    | 'light-pink'
    | 'light-purple'
    | 'light-orange'
    | 'light-aqua'
    | 'white'
}

const AvantImageBlock = ({
  content,
  size
}: {
  content: TestProps
  size: number
}) => {
  const { image, video, aspectRatio, brandDevicePosition, brandDeviceColour } =
    content
  const [sizeNum, setSizeNum] = useState(size)
  const position = brandDevicePosition || 'position-2'
  const accentColour = brandDeviceColour || 'light-blue'

  useEffect(() => {
    const updateSize = () => {
      if (window.innerWidth < 640) {
        setSizeNum(24)
      } else {
        setSizeNum(32)
      }
    }

    updateSize()
    window.addEventListener('resize', updateSize)

    return () => window.removeEventListener('resize', updateSize)
  }, [])

  // Check if transformations aren't null and gets width from w= or leave blank
  const imgWidth = '910'

  // Check if transformations aren't null and gets height from h= or leave blank
  const imgHeight = '910'

  return (
    <div
      className={cn(
        blockStyles({ svg: null, accentColour: null, aspect: aspectRatio }),
        'flex flex-col'
      )}
    >
      <div className='relative size-full object-cover'>
        <svg
          className={cn(
            blockStyles({
              svg: position,
              accentColour: accentColour,
              aspect: null
            }),
            'absolute'
          )}
          width={sizeNum}
          height={sizeNum}
        >
          <rect width={sizeNum} height={sizeNum} />
        </svg>

        {video ? (
          <div className='relative h-full w-full object-cover'>
            <BlockVideo
              videoUrl={video.sys.uri}
              width={imgWidth}
              height={imgHeight}
              image={image}
            />
          </div>
        ) : (
          <Image image={image} />
        )}
      </div>
      {image && image?.caption && (
        <div className='bg-grey-l p-xs-plus text-base-md text-blue-d -mt-[1px] w-full italic'>
          <MarkDown content={image?.caption} />
        </div>
      )}
    </div>
  )
}

export default AvantImageBlock
